import React from 'react';
import { useFamily } from '../../contexts/FamilyContext';

/**
 * SharingInfoDisplay - Read-only inline display of sharing scope info.
 * Only renders for multi-family users who own the content.
 *
 * Props:
 *   sharingScope     - 'private' | 'single_family' | 'all_families' | 'selected_families'
 *   originFamilyId   - UUID of the origin family (used to resolve name for single_family)
 *   sharedWithFamilies - array of family name strings (for selected_families scope)
 *   isOwner          - boolean, whether the current user owns this content
 *   className        - optional additional CSS classes
 */
function SharingInfoDisplay({
  sharingScope,
  originFamilyId,
  sharedWithFamilies = [],
  isOwner = false,
  className = '',
}) {
  const { isMultiFamily, families } = useFamily();

  // Only show for multi-family users who own the content
  if (!isMultiFamily || !isOwner) return null;

  // Don't show if no sharing scope is set
  if (!sharingScope) return null;

  // Resolve the origin family name from the families list
  const originFamily = families.find(f => f.id === originFamilyId);
  const originFamilyName = originFamily?.name || 'your family';

  let label = '';
  switch (sharingScope) {
    case 'private':
      label = 'Only you can see this';
      break;
    case 'single_family':
      label = `Visible to ${originFamilyName}`;
      break;
    case 'all_families':
      label = 'Visible to all your families';
      break;
    case 'selected_families':
      if (sharedWithFamilies.length > 0) {
        label = `Shared with: ${sharedWithFamilies.join(', ')}`;
      } else {
        label = 'Shared with selected families';
      }
      break;
    default:
      return null;
  }

  return (
    <div className={`flex items-center gap-1.5 text-sm text-gray-500 ${className}`}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="flex-shrink-0"
      >
        {sharingScope === 'private' ? (
          /* Lock icon for private */
          <>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </>
        ) : (
          /* Users/eye icon for shared */
          <>
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </>
        )}
      </svg>
      <span>{label}</span>
    </div>
  );
}

export default SharingInfoDisplay;
